import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { DownArrowIcon } from '../../../components/icon/icon';
import { AnimatePresence, motion } from 'framer-motion';
import { useHistory } from 'react-router-dom';
import { JobSearchSection } from '../../../components/user/job/user.job.section';
import { JobSearchCondition } from '../../../components/common/common.job.search.condition';
import {
  fetchCompanySubContractList,
  fetchSubContractList,
} from '../../../api/working';
import { useRecoilValue } from 'recoil';
import { authState } from '../../../atoms/authState';

interface SearchConditionProps {
  startDate: string;
  endDate: string;
  isDefaultRange?: boolean; // 기본 범위 여부 추가
}

interface SubContractResponse {
  subcontractId: number;
  target: string;
  state: string;
  createdAt: string;
}

const CommonJobSearch = () => {
  const auth = useRecoilValue(authState);

  const [loading, setLoading] = useState(true); // 로딩 상태 관리
  const [subContractList, setSubContractList] = useState<SubContractResponse[]>(
    [],
  );
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const history = useHistory();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchCondition, setSearchCondition] = useState<SearchConditionProps>({
    startDate: '',
    endDate: '',
    isDefaultRange: true, // 기본값을 true로 설정
  });

  const handleSearchClick = () => {
    setSearchOpen(true);
  };
  const handleSearchClose = () => {
    setSearchOpen(false);
  };
  const handleSearchSubmit = ({
    startDate,
    endDate,
    isDefaultRange,
  }: SearchConditionProps) => {
    setStartDate(startDate);
    setEndDate(endDate);
    setSearchCondition({ startDate, endDate, isDefaultRange }); // 상태 업데이트
    setSearchOpen(false);
  };
  const handleRegistClick = () => {
    history.push({
      pathname: '/working/regist',
      state: { borderLine: true },
    });
  };

  // 오늘 부터 일주일 이후 데이터 가져오기
  useEffect(() => {
    const getOrders = async () => {
      try {
        setLoading(true);

        const today = new Date();
        const weekLater = new Date(today);
        weekLater.setDate(today.getDate() + 7);

        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const startDateString = startDate || formatDate(today);
        const endDateString = endDate || formatDate(weekLater);

        let data;

        if (auth.userType === 'driver') {
          data = await fetchSubContractList({
            startDate: startDateString,
            endDate: endDateString,
          });
        } else if (auth.userType === 'company') {
          data = await fetchCompanySubContractList({
            startDate: startDateString,
            endDate: endDateString,
          });
        }

        setSubContractList(data || []); // data가 undefined인 경우 빈 배열로 설정
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    getOrders();
  }, [startDate, endDate, auth]);

  if (loading) return <p>Loading...</p>;

  if (!subContractList || subContractList.length === 0) {
    return (
      <Container>
        <SearchForm onClick={handleSearchClick}>
          <span>
            {searchCondition.startDate && searchCondition.endDate
              ? `${searchCondition.startDate} ~ ${searchCondition.endDate}`
              : '검색'}
          </span>
          <div className="icon-box">
            <DownArrowIcon />
          </div>
        </SearchForm>
        <JobSearchSection
          items={subContractList}
          registClick={handleRegistClick}
        />

        <AnimatePresence initial={false}>
          {searchOpen ? (
            <SearchConditionWrap
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                type: 'fade',
              }}
            >
              <JobSearchCondition
                handleSearchClose={handleSearchClose}
                handleSearchSubmit={handleSearchSubmit}
              />
            </SearchConditionWrap>
          ) : null}
        </AnimatePresence>
      </Container>
    );
  }

  return (
    <Container>
      <SearchForm onClick={handleSearchClick}>
        <span>
          {searchCondition.startDate && searchCondition.endDate
            ? searchCondition.isDefaultRange
              ? '전체'
              : `${searchCondition.startDate} ~ ${searchCondition.endDate}`
            : '검색'}
        </span>
        <div className="icon-box">
          <DownArrowIcon />
        </div>
      </SearchForm>
      <JobSearchSection
        items={subContractList}
        registClick={handleRegistClick}
      />
      <AnimatePresence initial={false}>
        {searchOpen ? (
          <SearchConditionWrap
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              type: 'fade',
            }}
          >
            <JobSearchCondition
              handleSearchClose={handleSearchClose}
              handleSearchSubmit={handleSearchSubmit}
            />
          </SearchConditionWrap>
        ) : null}
      </AnimatePresence>
    </Container>
  );
};

export default CommonJobSearch;

// style
const Container = styled.div`
  padding: 0 1.5rem 12.4rem;
  height: 100%;
  overflow: hidden;
  .no-data {
    margin-top: 2rem;
    font-size: 1.6rem;
    text-align: center;
  }
`;

const SearchForm = styled.div`
  background-color: ${(props) => props.theme.textWhiteColor};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 1.2rem;
  overflow: hidden;
  margin-top: 1.5rem;
  margin-bottom: 1.2rem;
  span {
    padding: 1.3rem;
    font-size: 1.3rem;
    font-weight: 600;
    line-height: 1.3rem;
    color: ${(props) => props.theme.textBlackColor};
  }
  .icon-box {
    display: flex;
    padding: 1.65rem;
  }
`;

const SearchConditionWrap = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: 'flex';
  justify-content: 'center';
  align-items: 'center';
  cursor: 'pointer';
  width: 100%;
  height: 100%;
  .overlay {
    display: flex;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
`;
